import React, {useState, useEffect, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsExporting from 'highcharts/modules/exporting';
import highchartsAccessibility from 'highcharts/modules/accessibility';
import parallelCoordinates from 'highcharts/modules/parallel-coordinates';

import InfoButton from "./InfoButton";

import styles from './sharedPageStyles.module.scss';
import miscHelper from "../ra_Helpers/miscHelper";

HighchartsMore(Highcharts);
HighchartsExporting(Highcharts);
highchartsAccessibility(Highcharts);
parallelCoordinates(Highcharts);

const ROTATION = {
  BEST: 0,
  REFERENCE: 1,
  WORST: 2,
};

const INDICATORS = miscHelper.sectionCIndicators();

const BestWorstChart = (props) => {
  const [chartOptions, setChartOptions] = useState({});
  const chartRef = useRef(null);
  const chartRenderDone = useRef(false);

  const parallelIndicators = useMemo(() => {
    const filteredIndicators = INDICATORS.filter(r => r.spider);
    filteredIndicators.sort((a, b) => a.spiderSort - b.spiderSort);

    return filteredIndicators;
  }, [INDICATORS]);

  const refRotation = props.Rotations.filter(r => r.BestWorst === 'REF');
  const bestRotation = props.Rotations.filter(r => r.BestWorst === 'BEST');
  const worstRotation = props.Rotations.filter(r => r.BestWorst === 'WORST');

  const referenceId = refRotation.length > 0 ? refRotation[0].BaseRotationId : null;
  const bestId = bestRotation.length > 0 ? bestRotation[0].BaseRotationId : null;
  const worstId = worstRotation.length > 0 ? worstRotation[0].BaseRotationId : null;

  const bestDescription = (indicator) => {
    let result = '';
    if (indicator) {
      const position = indicator.axisReverse ? 'Lowest' : 'Highest';
      const desc = indicator.display.toLowerCase();
      result = `${position} ${desc} rotation`;
    }

    return result;
  };

  const worstDescription = (indicator) => {
    let result = '';
    if (indicator) {
      const position = indicator.axisReverse ? 'Highest' : 'Lowest';
      const desc = indicator.display.toLowerCase();
      result = `${position} ${desc} rotation`;
    }

    return result;
  };

  const getSubTitle = () => {
    let subtitle = '(';
    if (props.SubTitle !== '') {
      subtitle += props.SubTitle;
      if (props.TopScale) {
        subtitle += '; top of scale is the better result';
      }
      subtitle += ')';
    }

    return subtitle;
  };

  useEffect(() => {
    const chartOpt = {
      chart: {
        type: 'spline',
        parallelCoordinates: true,
        parallelAxes: {
          labels: {
            style: {
              color: '#999999',
            }
          },
          gridLineWidth: 0,
          lineWidth: 2,
          showFirstLabel: true,
          showLastLabel: true
        },
        events: {
          render: function() {
            const chart = this;
            if (!chart.hasOwnProperty('customText')) {
              chart.customText = chart.renderer.text(chart_note)
                .css({
                  color: '#303030',
                  fontSize: '12px',
                  fontStyle: 'italic',
                })
                .add();

              chartRenderDone.current = true;
            }
          },
          redraw: function() {
            const chart = this;
            if (!chart.hasOwnProperty('customText')) {
              // Nothing to do if object doesn't exist yet.
            } else {
              const legendAttr = chart.legend.box.getBBox();
              const legendBox = chart.legend.box;
              const chartLegendX = legendBox.parentGroup.alignAttr.translateX;
              const chartLegendY = legendBox.parentGroup.alignAttr.translateY;

              const textX = chart.plotWidth / 2;
              const textY = chartLegendY + legendAttr.height + 5;
              chart.customText
                .attr({
                  text: chart_note,
                  align: 'center',
                  x: textX, // offset
                  verticalAlign: 'bottom',
                  y: textY, // offset,
                })
            }
          }
        },
      },
      title: {
        text: getTitle(),
      },
      subtitle: {
        text: getSubTitle(),
        style: {
          color: '#808080',
          fontSize: '0.7em',
        }
      },
      legend: {
        enabled: true,
      },
      tooltip: {
        pointFormat: '<span style="color:{point.color}">\u25CF</span>' +
          '{series.name}: <b>{point.formattedValue}</b><br/>'
      },
      plotOptions: {
        series: {
          accessibility: {
            enabled: false
          },
          animation: false,
          marker: {
            enabled: false,
            states: {
              hover: {
                enabled: false
              }
            }
          },
          states: {
            hover: {
              halo: {
                size: 0
              }
            }
          },
          events: {
            mouseOver: function () {
              this.group.toFront();
            },
          }
        }
      },
      xAxis: {
        categories: makeIndicatorCategories(),
        offset: 10,
      },
      yAxis: configureYAxis(),
      colors: ['rgba(11, 200, 200, 0.1)'],
      series: getChartData(),
    }

    setChartOptions(chartOpt);

  }, [props.Rotations, props.IndicatorObj]);

  const getTitle = () => {
    if (props.Title === '') {
      return 'Multi-criteria performance';
    } else {
      return props.Title;
    }
  };

  const configureYAxis = () => {
    let yaxis = [];

    parallelIndicators.forEach(ind => {
      const indicator = ind.value;


      let tooltipFormat = '{value}';
      if (ind.units !== '') {
        tooltipFormat = `{value} (${ind.units})`;
      }

      const axisConfig = {
        name: indicator,
        labels: {
          format: '{value}',
          align: 'right',
          x: -5,
        },
        ticks: 4,
        tickWidth: 1,
        tickLength: 3,
        tooltipValueFormat: tooltipFormat,
        reversed: ind.axisReverse,
      }

      if (ind.hasOwnProperty('minAxisValue')) {
        axisConfig.min = ind.minAxisValue;
      }

      if (ind.hasOwnProperty('maxAxisValue')) {
        axisConfig.min = ind.maxAxisValue;
      }

      yaxis.push(axisConfig);
    });

    return yaxis;
  };

  const makeIndicatorCategories = () =>  {
    const categories = [];

    parallelIndicators.forEach(indicator => {
      let catDisplay = indicator.display;
      if (indicator.units !== '') {
        catDisplay += ' (' + indicator.units + ')';
      }
      categories.push(catDisplay);
    });

    return categories;
  }

  const makeSeriesArray = (rotationObj, seriesName) => {
    let ary = [];

    if (rotationObj) {
      // Gross Margin for this rotation.
      const {gross_margin} = rotationObj;

      parallelIndicators.forEach(ind => {
        const indicator = ind.value;
        if (rotationObj[indicator] !== null) {
          const roundedValue = Math.round(rotationObj[indicator] * 1000) / 1000;
          ary.push(roundedValue);
        } else {
          // In certain circumstances, an indicator value may not be 
          // present in the rotation object. We want to know about these
          // and the parallel coordinates chart makes this obvious.
          // However, a known situation for downside risk we specifically
          // want to use a value of zero instead of null.
          let value = null;
          if (indicator === 'downside_risk') { 
            if (typeof gross_margin !== 'undefined') {
              if (gross_margin === 0) {
                value = 0;
              }  
            }  
          } 
          ary.push(value);
        }
      });
    }

    return ary;
  };

  const getChartData = () => {
    const series = [];

    let refRotationId = null;
    if (refRotation.length > 0) {
      refRotationId = refRotation[0].BaseRotationId;
      const dataSeriesObj = {
        name: 'Reference rotation',
        color: 'blue',
        data: [...makeSeriesArray(refRotation[0])],
        shadow: false,
      };
      series.push(dataSeriesObj);
    }
    if (bestRotation.length > 0) {
      const bestRotationId = bestRotation[0].BaseRotationId;
      // Show best rotation if not same as reference
      if (bestRotationId !== refRotationId) {
        const dataSeriesObj = {
          name: bestDescription(props.IndicatorObj),
          color: 'green',
          data: [...makeSeriesArray(bestRotation[0])],
          shadow: false,
        };
        series.push(dataSeriesObj);
      }
    }
    if (worstRotation.length > 0) {
      const worstRotationId = worstRotation[0].BaseRotationId;    
      // Show worst rotation if not same as reference
      if (worstRotationId !== refRotationId) {
        const dataSeriesObj = {
          name: worstDescription(props.IndicatorObj),
          color: 'red',
          data: [...makeSeriesArray(worstRotation[0])],
          shadow: false,
        };
        series.push(dataSeriesObj);
      }
    }

    // console.log('BestWorst data', series);

    return series;
  };

  let chart_note = '';
  if (referenceId === worstId) {
    chart_note += `Reference rotation = ${worstDescription(props.IndicatorObj)}`;
  }
  if (referenceId === bestId) {
    if (chart_note !== '') {
      chart_note += ', ';
    }
    chart_note += `Reference rotation = ${bestDescription(props.IndicatorObj)}`;
  }

  return (
    <div className={styles.graphContainer}>
      <div>
        {props.Rotations && <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={chartOptions}
          constructorType={'chart'}
        />}
      </div>
      <div className={styles.chartExplanation}>
        {props.Explanation}
      </div>
    </div>
  );
}

BestWorstChart.propTypes = {
  Rotations: PropTypes.array.isRequired,
  IndicatorObj: PropTypes.object.isRequired,
  Title: PropTypes.string,
  Explanation: PropTypes.string,
  InfoKey: PropTypes.string,
  SubTitle: PropTypes.string,
  TopScale: PropTypes.bool,
};

BestWorstChart.defaultProps = {
  Title: '',
  Explanation: '',
  InfoKey: '',
  SubTitle: '',
  TopScale: false,
};
export default BestWorstChart;
